/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Button,
  useDisclosure,
  IconButton,
  Tooltip,
  FormControl,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Text,
  Icon,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import { Select } from 'chakra-react-select';
import moment from 'moment';
import { FunnelSimple, X, GridFour, Table } from 'phosphor-react';
import { Calendar } from 'lucide-react';
import { MonthNavigator, CustomDateRangePicker } from '../../../../../components/shared';
import { InputForm } from '../../../../../components/inputs/forms/InputForm';
import SelectInputForm from '../../../../../components/inputs/forms/SelectInputForm';
import MultiSelectInputForm from '../../../../../components/inputs/forms/MultiSelectInputForm';
import { FilterDrawer } from '../../../../../components/FilterDrawer';
import { ISelect } from '../../../../../types/shared';
import { useCompanies } from '../../../../../hooks/useCompanies';
import { getFormattedLabelString } from '../../../../../utils/FormatFunctions';
import { FilterFormValues, PeriodType } from '../../../../../hooks/useTaxFilters';
import { PeriodButtonsMontly } from '../../../../../components/shared/PeriodButtonsMonthly';
import {
  DropdownSearch,
  ISearchDropdownItem,
} from '../../../../../components/shared/DropdownSearch';

// Função para formatar período
const formatPeriodo = (dateStart?: string, dateEnd?: string) => {
  if (!dateStart || !dateEnd) return null;
  const start = moment(dateStart).format('DD/MM/YYYY');
  const end = moment(dateEnd).format('DD/MM/YYYY');
  return `${start} a ${end}`;
};

// Estilos minimalistas para Select (estilo Apple)
const selectChakraStyles = {
  control: (styles: any) => ({
    ...styles,
    minH: '48px',
    backgroundColor: '#FAFAFA',
    borderColor: '#E5E5E5',
    borderRadius: '12px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#D1D1D1',
    },
  }),
  menuList: (provided: any) => ({
    ...provided,
    zIndex: 9999,
    bg: 'white',
    maxH: '200px',
    borderColor: '#E5E5E5',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    padding: '4px',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    borderRadius: '8px',
    backgroundColor: state.isSelected ? '#F5F5F5' : 'transparent',
    color: '#1D1D1F',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    bg: 'transparent',
    color: '#86868B',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: '#86868B',
  }),
};

interface TaxFiltersProps {
  filters: URLSearchParams;
  currentFilterDate: moment.Moment;
  selectedPeriod: PeriodType;
  useCustomRange: boolean;
  customDateStart: string;
  customDateEnd: string;
  activeFiltersCount: number;
  viewMode: 'grid' | 'table';
  // eslint-disable-next-line no-unused-vars
  onViewModeChange: (mode: 'grid' | 'table') => void;
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  // eslint-disable-next-line no-unused-vars
  handleSelectMonth: (year: number, month: number) => void;
  handleQuarterPeriod: () => void;
  handleSemesterPeriod: () => void;
  handleYearPeriod: () => void;
  handleCurrentYearPeriod: () => void;
  handleLastYearPeriod: () => void;
  handleApplyCustomRange: () => void;
  handleResetToMonth: () => void;
  // eslint-disable-next-line no-unused-vars
  setCustomDateStart: (date: string) => void;
  // eslint-disable-next-line no-unused-vars
  setCustomDateEnd: (date: string) => void;
  // eslint-disable-next-line no-unused-vars
  handleApplyFilters: (values: FilterFormValues, tipoRegime?: string) => void;
  handleClearFilters: () => void;
  // eslint-disable-next-line no-unused-vars
  onSearchChange: (search: string, searchType: string) => void;
}

export function TaxFilters({
  filters,
  currentFilterDate,
  selectedPeriod,
  useCustomRange,
  customDateStart,
  customDateEnd,
  activeFiltersCount,
  viewMode,
  onViewModeChange,
  handlePrevMonth,
  handleNextMonth,
  handleSelectMonth,
  handleQuarterPeriod,
  handleSemesterPeriod,
  handleYearPeriod,
  handleCurrentYearPeriod,
  handleLastYearPeriod,
  handleApplyCustomRange,
  handleResetToMonth,
  setCustomDateStart,
  setCustomDateEnd,
  handleApplyFilters,
  handleClearFilters,
  onSearchChange,
}: TaxFiltersProps) {
  const { getCompaniesFilters } = useCompanies();

  const searchFiltersDropdown: ISearchDropdownItem[] = [
    { label: 'ID', value: 'external_id' },
    { label: 'CNPJ', value: 'cnpj' },
    { label: 'Razão Social', value: 'razao_social' },
  ];

  const regimeOptions: ISearchDropdownItem[] = [
    { label: 'Lucro Presumido', value: 'lucro_presumido' },
    { label: 'Lucro Real', value: 'lucro_real' },
  ];

  const [selectedFilter, setSelectedFilter] = useState<ISearchDropdownItem>(
    searchFiltersDropdown[0],
  );

  // Modo visual do filtro de data dentro do popover
  const [dateMode, setDateMode] = useState<'month' | 'quick' | 'custom'>('month');

  // Estado local para o regime tributário selecionado no drawer
  const currentRegimeValue = filters.get('tipo_regime') || '';
  const [localSelectedRegime, setLocalSelectedRegime] = useState<ISearchDropdownItem | null>(
    regimeOptions.find(opt => opt.value === currentRegimeValue) || null,
  );

  const handleFilterChange = (value: ISearchDropdownItem | null) => {
    if (value) {
      setSelectedFilter(value);
    }
  };

  // Atualiza estado local sem aplicar filtro imediatamente
  const onRegimeChange = (value: ISearchDropdownItem | null) => {
    setLocalSelectedRegime(value);
  };

  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();

  // Sincroniza o estado local quando o drawer abre ou quando os filtros mudam
  useEffect(() => {
    const currentRegime = filters.get('tipo_regime') || '';
    const matchingOption = regimeOptions.find(opt => opt.value === currentRegime) || null;
    setLocalSelectedRegime(matchingOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterDrawerOpen, filters]);

  const filtersFormMethods = useForm<FilterFormValues & { search: string }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
    },
  });

  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    onSearchChange(searchValue, selectedFilter.value);
  }, 500);

  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods, debouncedSearch, selectedFilter]);

  const hasActiveFilters = activeFiltersCount > 0;

  const queryKeyCompaniesFilters = ['CompaniesFilters-taxAnalysis'];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  const onApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    // Aplica todos os filtros incluindo o regime tributário em uma única chamada
    handleApplyFilters(values, localSelectedRegime?.value || '');
    onFilterDrawerClose();
  };

  const onClearFilters = () => {
    handleClearFilters();
    setLocalSelectedRegime(null);
    filtersFormMethods.reset({
      search: filters.get('search') || '',
    });
  };

  return (
    <Box mb={6} p={{ base: 2, md: 4 }} bg="gray.50" borderRadius="lg">
      <FormProvider {...filtersFormMethods}>
        <Flex direction="column" gap={{ base: 2, md: 4 }}>
          {/* Linha principal: busca à esquerda, Data/Filtros à direita */}
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            gap={{ base: 2, md: 3 }}
            justify="space-between"
            w="100%"
          >
            <HStack spacing={2} flexWrap={{ base: 'wrap', md: 'nowrap' }} flex="1 1 auto" minW={0}>
              <DropdownSearch
                options={searchFiltersDropdown}
                value={selectedFilter}
                onChange={handleFilterChange}
              />
              <Box
                minW={{ base: '100%', md: '250px' }}
                maxW={{ base: '100%', md: '300px' }}
                flex={{ base: '1', md: 'none' }}
              >
                <InputForm
                  name="search"
                  placeholder="Buscar por nome, CNPJ, razao social..."
                  boxShadow="0px 2px 4px #1E293B0D"
                  controlMb={0}
                />
              </Box>
              {filtersFormMethods.watch('search') && (
                <Tooltip label="Limpar busca" hasArrow>
                  <IconButton
                    aria-label="Limpar busca"
                    icon={<X size={16} />}
                    size="md"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => {
                      filtersFormMethods.setValue('search', '');
                      onSearchChange('', selectedFilter.value);
                    }}
                  />
                </Tooltip>
              )}
            </HStack>

            <HStack
              spacing={{ base: 2, md: 3 }}
              flexWrap="wrap"
              justify={{ base: 'flex-start', lg: 'flex-end' }}
              align="center"
            >
              {/* Exibir período do lado esquerdo do botão Data */}
              {formatPeriodo(
                filters.get('date_start') || undefined,
                filters.get('date_end') || undefined,
              ) && (
                <Text fontSize="xs" color="gray.600" fontWeight="500" whiteSpace="nowrap">
                  Período:{' '}
                  {formatPeriodo(
                    filters.get('date_start') || undefined,
                    filters.get('date_end') || undefined,
                  )}
                </Text>
              )}
              {/* Filtro de Data como botão clicável com modos organizados */}
              <Popover
                placement="bottom-start"
                onOpen={() => {
                  // Sincroniza o modo visual com o estado atual de período
                  if (useCustomRange) {
                    setDateMode('custom');
                  } else if (selectedPeriod === 'month') {
                    setDateMode('month');
                  } else {
                    setDateMode('quick');
                  }
                }}
              >
                <PopoverTrigger>
                  <Button variant="outline" leftIcon={<Calendar size={16} />} size="sm" bg="white">
                    Data
                  </Button>
                </PopoverTrigger>
                <PopoverContent w={{ base: 'calc(100vw - 32px)', sm: 'auto' }} maxW="640px">
                  <PopoverBody p={3}>
                    <Flex direction="column" gap={3}>
                      {/* Abas simples para trocar entre os modos de data */}
                      <HStack spacing={2} flexWrap="wrap">
                        <Button
                          size="xs"
                          variant={dateMode === 'month' ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => setDateMode('month')}
                          flex={{ base: '1 1 auto', sm: '0 0 auto' }}
                          minW={{ base: '100px', sm: 'auto' }}
                        >
                          Mês / Ano
                        </Button>
                        <Button
                          size="xs"
                          variant={dateMode === 'quick' ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => setDateMode('quick')}
                          flex={{ base: '1 1 auto', sm: '0 0 auto' }}
                          minW={{ base: '100px', sm: 'auto' }}
                        >
                          Períodos rápidos
                        </Button>
                        <Button
                          size="xs"
                          variant={dateMode === 'custom' ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => setDateMode('custom')}
                          flex={{ base: '1 1 auto', sm: '0 0 auto' }}
                          minW={{ base: '100px', sm: 'auto' }}
                        >
                          Período personalizado
                        </Button>
                      </HStack>

                      {/* Conteúdo dos modos de data - exibidos de forma isolada */}
                      {dateMode === 'month' && (
                        <Box w="100%">
                          <HStack spacing={1} flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                            <Box
                              flex={{ base: '1 1 100%', sm: '0 0 auto' }}
                              minW={{ base: '100%', sm: 'auto' }}
                            >
                              <MonthNavigator
                                currentDate={currentFilterDate}
                                onPrevMonth={handlePrevMonth}
                                onNextMonth={handleNextMonth}
                                onSelectMonth={handleSelectMonth}
                                isDisabled={selectedPeriod !== 'month' || useCustomRange}
                              />
                            </Box>
                            {(selectedPeriod !== 'month' || useCustomRange) && (
                              <Tooltip label="Voltar para mês atual" hasArrow>
                                <IconButton
                                  aria-label="Limpar filtro de período"
                                  icon={<X size={16} />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={handleResetToMonth}
                                  flexShrink={0}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Box>
                      )}

                      {dateMode === 'quick' && (
                        <Box w="100%">
                          <PeriodButtonsMontly
                            selectedPeriod={selectedPeriod}
                            onQuarterClick={handleQuarterPeriod}
                            onSemesterClick={handleSemesterPeriod}
                            onYearClick={handleYearPeriod}
                            onCurrentYearClick={handleCurrentYearPeriod}
                            onLastYearClick={handleLastYearPeriod}
                          />
                        </Box>
                      )}

                      {dateMode === 'custom' && (
                        <Box w="100%">
                          <CustomDateRangePicker
                            isActive={useCustomRange}
                            dateStart={customDateStart}
                            dateEnd={customDateEnd}
                            onDateStartChange={setCustomDateStart}
                            onDateEndChange={setCustomDateEnd}
                            onApply={handleApplyCustomRange}
                          />
                        </Box>
                      )}
                    </Flex>
                  </PopoverBody>
                </PopoverContent>
              </Popover>

              <HStack
                spacing={1}
                bg="white"
                px={1}
                borderRadius="md"
                border="1px"
                borderColor="gray.200"
              >
                <IconButton
                  aria-label="Visualização em tabela"
                  icon={<Icon as={Table} />}
                  onClick={() => onViewModeChange('table')}
                  size="sm"
                  variant={viewMode === 'table' ? 'solid' : 'ghost'}
                  colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
                />
                <IconButton
                  aria-label="Visualização em grid"
                  icon={<Icon as={GridFour} />}
                  onClick={() => onViewModeChange('grid')}
                  size="sm"
                  variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                  colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
                />
              </HStack>

              <Button
                onClick={onFilterDrawerOpen}
                leftIcon={<FunnelSimple size={16} />}
                variant="outline"
                colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                size="sm"
              >
                Filtros{hasActiveFilters && ` (${activeFiltersCount})`}
              </Button>
            </HStack>
          </Flex>
        </Flex>

        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={onFilterDrawerClose}
          title="Filtros Avancados"
          hasActiveFilters={hasActiveFilters}
          onClearFilters={onClearFilters}
          onApplyFilters={onApplyFilters}
          formMethods={filtersFormMethods}
          size="lg"
        >
          <Flex direction="column" gap={6}>
            {/* Regime Tributário */}
            <Box>
              <Box
                as="span"
                fontSize="xs"
                fontWeight="600"
                color="#86868B"
                textTransform="uppercase"
                letterSpacing="0.5px"
                mb={2}
                display="block"
              >
                Regime Tributário
              </Box>
              <FormControl>
                <Select
                  options={regimeOptions}
                  value={localSelectedRegime}
                  onChange={onRegimeChange}
                  placeholder="Selecione o regime"
                  isClearable
                  chakraStyles={selectChakraStyles}
                />
              </FormControl>
            </Box>

            {/* Localização */}
            <Box>
              <Box
                as="span"
                fontSize="xs"
                fontWeight="600"
                color="#86868B"
                textTransform="uppercase"
                letterSpacing="0.5px"
                mb={2}
                display="block"
              >
                Localização
              </Box>
              <Flex gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                <Box flex={1} minW={{ base: '100%', md: '0' }}>
                  <SelectInputForm<ISelect<string>>
                    name="cidade"
                    placeholder="Cidade"
                    options={getFormattedLabelString(CompaniesFilters?.cidade)}
                    controlMb={0}
                  />
                </Box>
                <Box flex={1} minW={{ base: '100%', md: '0' }}>
                  <SelectInputForm<ISelect<string>>
                    name="uf"
                    placeholder="Estado"
                    options={getFormattedLabelString(CompaniesFilters?.uf)}
                    controlMb={0}
                  />
                </Box>
              </Flex>
            </Box>

            {/* Organização */}
            <Box>
              <Box
                as="span"
                fontSize="xs"
                fontWeight="600"
                color="#86868B"
                textTransform="uppercase"
                letterSpacing="0.5px"
                mb={2}
                display="block"
              >
                Organização
              </Box>
              <Flex gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                <Box flex={1} minW={{ base: '100%', md: '0' }}>
                  <MultiSelectInputForm<ISelect<string>>
                    name="grupo_de_empresas"
                    placeholder="Selecione até 4 grupos"
                    options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                    maxSelectedOptions={4}
                    controlMb={0}
                  />
                </Box>
                <Box flex={1} minW={{ base: '100%', md: '0' }}>
                  <SelectInputForm<ISelect<string>>
                    name="tags"
                    placeholder="Tags"
                    options={getFormattedLabelString(CompaniesFilters?.tags)}
                    controlMb={0}
                  />
                </Box>
              </Flex>
            </Box>

            {/* Responsáveis */}
            <Box>
              <Box
                as="span"
                fontSize="xs"
                fontWeight="600"
                color="#86868B"
                textTransform="uppercase"
                letterSpacing="0.5px"
                mb={2}
                display="block"
              >
                Responsáveis
              </Box>
              <Flex direction="column" gap={3}>
                <Flex gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="comercial"
                      placeholder="Comercial"
                      options={getFormattedLabelString(CompaniesFilters?.comercial)}
                      controlMb={0}
                    />
                  </Box>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="contabil"
                      placeholder="Contábil"
                      options={getFormattedLabelString(CompaniesFilters?.contabil)}
                      controlMb={0}
                    />
                  </Box>
                </Flex>
                <Flex gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="financeiro"
                      placeholder="Financeiro"
                      options={getFormattedLabelString(CompaniesFilters?.financeiro)}
                      controlMb={0}
                    />
                  </Box>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="fiscal"
                      placeholder="Fiscal"
                      options={getFormattedLabelString(CompaniesFilters?.fiscal)}
                      controlMb={0}
                    />
                  </Box>
                </Flex>
                <Flex gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="pessoal"
                      placeholder="Pessoal"
                      options={getFormattedLabelString(CompaniesFilters?.pessoal)}
                      controlMb={0}
                    />
                  </Box>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="societario"
                      placeholder="Societário"
                      options={getFormattedLabelString(CompaniesFilters?.societario)}
                      controlMb={0}
                    />
                  </Box>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="contas_a_pagar"
                      placeholder="Contas a Pagar"
                      options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
                      controlMb={0}
                    />
                  </Box>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="encerramento_anual"
                      placeholder="Encerramento Anual"
                      options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
                      controlMb={0}
                    />
                  </Box>
                </Flex>
                <Flex gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="imposto_de_renda"
                      placeholder="Imposto de Renda"
                      options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
                      controlMb={0}
                    />
                  </Box>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="relacionamento_com_cliente"
                      placeholder="Relacionamento"
                      options={getFormattedLabelString(
                        CompaniesFilters?.relacionamento_com_cliente,
                      )}
                      controlMb={0}
                    />
                  </Box>
                </Flex>
                <Flex gap={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="integracao_contabil"
                      placeholder="Integração Contábil"
                      options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
                      controlMb={0}
                    />
                  </Box>
                  <Box flex={1} minW={{ base: '100%', md: '0' }}>
                    <SelectInputForm<ISelect<string>>
                      name="integracao_fiscal"
                      placeholder="Integração Fiscal"
                      options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
                      controlMb={0}
                    />
                  </Box>
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </FilterDrawer>
      </FormProvider>
    </Box>
  );
}
